import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getOCPPRemoteStopTransactionRequestDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-stop-transaction.form-default-values';
import { OCPPRemoteStopTransactionRequestModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-stop-transaction.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoStopTransactionOcppMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';

import {
  StopChargePointChannelTransactionContextProvider,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelTransactionStopFormController } from './charge-point-channel-transaction-stop-form/charge-point-channel-transaction-stop-form.controller';

interface ChargePointChannelTransactionStopControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelTransactionStopController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelTransactionStopControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [stopTransactionOcppMutation] = useCpmsBoStopTransactionOcppMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<OCPPRemoteStopTransactionRequestModel, 'chargePointId' | 'channelId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-transaction-stop.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<OCPPRemoteStopTransactionRequestModel, 'chargePointId' | 'channelId'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    stopTransactionOcppMutation({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-channel-transaction-stop.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-transaction-stop.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getOCPPRemoteStopTransactionRequestDefaultValues(
    chargePoint.id,
    channel.id,
  );

  return (
    <StopChargePointChannelTransactionContextProvider data={data}>
      <ChargePointChannelTransactionStopFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </StopChargePointChannelTransactionContextProvider>
  );
};
